import React, { useState } from "react";
import { Plus, Edit, Trash2, FileText, CheckCircle2, AlertCircle, ChevronDown, ChevronUp, Save, X, ArrowUp, ArrowDown } from "lucide-react";
import { useAgreements } from "../../hooks/useAgreements";
import { DEFAULT_TEAMING_SECTIONS } from "../../services/agreementsService";

export default function AgreementTemplatesSettings() {
  const {
    templates,
    loading,
    error,
    addTemplate,
    updateTemplate,
    deleteTemplate
  } = useAgreements();

  // ID of currently expanded template (or "new" for new template form)
  const [expandedTemplateId, setExpandedTemplateId] = useState(null);
  const [statusMsg, setStatusMsg] = useState({ type: "", text: "" });
  const [isSaving, setIsSaving] = useState(false);

  // Form states for the currently edited template
  const [editFormData, setEditFormData] = useState({
    title: "",
    description: "",
    preambleTemplate: "",
    recitals: [],
    defaultLatNotices: "",
    defaultPartnerNotices: "",
    sections: []
  });

  const handleOpenAdd = () => {
    setExpandedTemplateId("new");
    setEditFormData({
      title: "",
      description: "",
      preambleTemplate: `THIS TEAMING AGREEMENT, including all Exhibits attached hereto or referenced herein (hereinafter referred to as “this Agreement”), is made and entered into this {{day}} day of {{month}}, {{year}}, by and between Longarm Technologies, LLC (“LAT”), a Nevada corporation with offices located at 1543 Frew mill Rd News Castle PA, and {{partnerName}} a {{partnerState}} corporation with offices located at {{partnerAddress}}. These two entities are sometimes referred to collectively herein as the “Team or Parties” and individually as a “Party.”`,
      recitals: [
        `WHEREAS, the {{customerName}}, herein referred to as “Customer” or {{customerAcronym}} is planning to issue solicitations (“Solicitations”) for the acquisition of {{procurementDescription}} (“Procurement”).`,
        `WHEREAS, each of the Parties has determined that it would benefit from a team arrangement in order to develop the optimal approach to responding to each Solicitation, and because of each Party’s unique capabilities, the Parties recognize the efficiency of teaming together and therefore wish to team together for the purpose of competitively responding to each Solicitation and obtaining contract(s) and/or purchase/task order(s) resulting therefrom (herein referred to as “Orders”);`,
        `WHEREAS, the Parties wish to enter into this Agreement to set forth more fully the terms and conditions pursuant to which the Parties will, as a Team, respond to the Solicitations and obtain any Order(s) resulting therefrom, and the Parties have agreed to the respective responsibilities of work to be performed by each Party on the Procurement, as set forth in the Solicitation; and`,
        `WHEREAS, the ability of the Parties to competitively respond to the Solicitations and to obtain contracts resulting therefrom will necessitate the disclosure by each Party to the other of its Proprietary Information, and the Parties are willing to make their Proprietary Information available to the other Party for the limited purpose of competitively responding to the Solicitations and obtaining order(s) resulting therefrom, and each of the Parties is willing to accept the Proprietary Information from the other Party in confidence for use solely and exclusively in connection with competitively responding to the Solicitations and obtaining orders(s) resulting therefrom, in accordance with the terms and conditions of this Agreement and any Non-Disclosure Agreement the Parties have entered into.`
      ],
      defaultLatNotices: `Longarm Technologies, LLC\n1543 Frew mill Rd News Castle PA\nAttention: Dale Flippo/James Pasqualini\nPhone: (866) 668-6060\nemail: support@longarmtechnologies.com`,
      defaultPartnerNotices: `{{partnerCompany}}\n{{partnerAddress}}\nAttention: {{partnerPocName}}\nPhone: {{partnerPocPhone}}\nemail: {{partnerPocEmail}}`,
      sections: DEFAULT_TEAMING_SECTIONS
    });
  };

  const handleOpenEdit = (tpl) => {
    if (expandedTemplateId === tpl.id) {
      // Toggle close
      setExpandedTemplateId(null);
      return;
    }
    setExpandedTemplateId(tpl.id);
    setEditFormData({
      title: tpl.title || "",
      description: tpl.description || "",
      preambleTemplate: tpl.preambleTemplate || "",
      recitals: tpl.recitals || [],
      defaultLatNotices: tpl.defaultLatNotices || `Longarm Technologies, LLC\n1543 Frew mill Rd News Castle PA\nAttention: Dale Flippo/James Pasqualini\nPhone: (866) 668-6060\nemail: support@longarmtechnologies.com`,
      defaultPartnerNotices: tpl.defaultPartnerNotices || `{{partnerCompany}}\n{{partnerAddress}}\nAttention: {{partnerPocName}}\nPhone: {{partnerPocPhone}}\nemail: {{partnerPocEmail}}`,
      sections: tpl.sections || DEFAULT_TEAMING_SECTIONS
    });
  };

  const handleCloseEditor = () => {
    setExpandedTemplateId(null);
  };

  const handleDelete = async (tpl) => {
    if (tpl.isDefault) {
      alert("The default Teaming Agreement template cannot be deleted.");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete template "${tpl.title}"?`)) {
      return;
    }
    try {
      await deleteTemplate(tpl.id);
      if (expandedTemplateId === tpl.id) setExpandedTemplateId(null);
      setStatusMsg({ type: "success", text: `Template "${tpl.title}" deleted.` });
      setTimeout(() => setStatusMsg({ type: "", text: "" }), 3000);
    } catch (err) {
      console.error("Error deleting template:", err);
      setStatusMsg({ type: "error", text: "Failed to delete template: " + (err.message || err) });
    }
  };

  // Recitals handlers
  const handleAddRecital = () => {
    setEditFormData({
      ...editFormData,
      recitals: [...editFormData.recitals, "WHEREAS, "]
    });
  };

  const handleRemoveRecital = (idx) => {
    setEditFormData({
      ...editFormData,
      recitals: editFormData.recitals.filter((_, i) => i !== idx)
    });
  };

  const handleUpdateRecital = (idx, val) => {
    const next = [...editFormData.recitals];
    next[idx] = val;
    setEditFormData({ ...editFormData, recitals: next });
  };

  // Sections handlers
  const handleAddSection = () => {
    const nextNum = String(editFormData.sections.length + 1);
    setEditFormData({
      ...editFormData,
      sections: [
        ...editFormData.sections,
        {
          id: `sec_${Date.now()}`,
          sectionNumber: nextNum,
          title: "New Section",
          content: ""
        }
      ]
    });
  };

  const handleRemoveSection = (idx) => {
    const next = editFormData.sections
      .filter((_, i) => i !== idx)
      .map((s, i) => ({ ...s, sectionNumber: String(i + 1) }));
    setEditFormData({ ...editFormData, sections: next });
  };

  const handleMoveSection = (idx, direction) => {
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= editFormData.sections.length) return;
    const next = [...editFormData.sections];
    const [moved] = next.splice(idx, 1);
    next.splice(targetIdx, 0, moved);
    const renumbered = next.map((s, i) => ({ ...s, sectionNumber: String(i + 1) }));
    setEditFormData({ ...editFormData, sections: renumbered });
  };

  const handleUpdateSection = (idx, field, val) => {
    const next = [...editFormData.sections];
    next[idx] = { ...next[idx], [field]: val };
    setEditFormData({ ...editFormData, sections: next });
  };

  const handleSaveSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData.title.trim()) {
      alert("Please enter a template title.");
      return;
    }
    setIsSaving(true);
    try {
      if (expandedTemplateId === "new") {
        await addTemplate({
          ...editFormData,
          isDefault: false
        });
        setStatusMsg({ type: "success", text: `Template "${editFormData.title}" created successfully.` });
      } else {
        const currentTpl = templates.find((t) => t.id === expandedTemplateId);
        await updateTemplate(expandedTemplateId, {
          ...editFormData,
          isDefault: currentTpl?.isDefault || false
        });
        setStatusMsg({ type: "success", text: `Template "${editFormData.title}" updated successfully.` });
      }
      setExpandedTemplateId(null);
      setTimeout(() => setStatusMsg({ type: "", text: "" }), 3000);
    } catch (err) {
      console.error("Error saving template:", err);
      setStatusMsg({ type: "error", text: "Failed to save template: " + (err.message || err) });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="card glass-card" style={{ padding: "1.5rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <div>
          <h3 className="card-title" style={{ display: "flex", alignItems: "center", margin: 0 }}>
            <FileText size={18} style={{ marginRight: "0.5rem", color: "var(--primary)" }} />
            Master Agreement Templates
          </h3>
          <p className="text-secondary" style={{ fontSize: "0.85rem", margin: "0.3rem 0 0 0" }}>
            Click "Edit Clauses" to fold out the sections and modify legal boilerplate, recitals, points of contact, and terms inline.
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={handleOpenAdd}
          disabled={expandedTemplateId === "new"}
        >
          <Plus size={16} style={{ marginRight: "0.25rem" }} /> Add Template
        </button>
      </div>

      {statusMsg.text && (
        <div className={`alert alert-${statusMsg.type === "error" ? "danger" : "success"}`} style={{ marginBottom: "1rem" }}>
          {statusMsg.text}
        </div>
      )}

      {/* NEW TEMPLATE INLINE FORM */}
      {expandedTemplateId === "new" && (
        <div
          className="animate-fade-in"
          style={{
            padding: "1.25rem",
            marginBottom: "1.5rem",
            border: "2px solid var(--primary)",
            borderRadius: "8px",
            backgroundColor: "rgba(59, 130, 246, 0.03)"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", borderBottom: "1px solid var(--border-color)", paddingBottom: "0.75rem" }}>
            <h4 style={{ margin: 0, color: "var(--primary)", fontSize: "1.05rem", fontWeight: 700 }}>
              Create New Master Agreement Template
            </h4>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseEditor}
              style={{ padding: "0.25rem 0.6rem", fontSize: "0.8rem" }}
            >
              <X size={14} /> Cancel
            </button>
          </div>

          <form onSubmit={handleSaveSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontWeight: 600 }}>Template Title *</label>
                <input
                  type="text"
                  className="form-input"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  placeholder="e.g. Non-Disclosure Agreement"
                  required
                />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontWeight: 600 }}>Description</label>
                <input
                  type="text"
                  className="form-input"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  placeholder="Brief description of this template..."
                />
              </div>
            </div>

            {/* Preamble */}
            <div className="card glass-card" style={{ padding: "1rem", marginBottom: "1rem", border: "1px solid var(--border-color)" }}>
              <label className="form-label" style={{ fontWeight: 700, color: "var(--primary)", marginBottom: "0.3rem" }}>
                Preamble Opening Statement
              </label>
              <p style={{ fontSize: "0.75rem", color: "var(--text-secondary)", margin: "0 0 0.4rem 0" }}>
                Supports placeholders: {"{{day}}"}, {"{{month}}"}, {"{{year}}"}, {"{{partnerName}}"}, {"{{partnerState}}"}, {"{{partnerAddress}}"}
              </p>
              <textarea
                className="form-input"
                rows={3}
                value={editFormData.preambleTemplate}
                onChange={(e) => setEditFormData({ ...editFormData, preambleTemplate: e.target.value })}
                style={{ fontSize: "0.82rem", lineHeight: 1.4 }}
              />
            </div>

            {/* Recitals */}
            <div className="card glass-card" style={{ padding: "1rem", marginBottom: "1rem", border: "1px solid var(--border-color)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                <label className="form-label" style={{ fontWeight: 700, color: "var(--primary)", margin: 0 }}>
                  Recitals (WHEREAS Clauses)
                </label>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem" }}
                  onClick={handleAddRecital}
                >
                  <Plus size={13} /> Add Recital
                </button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {editFormData.recitals.map((rec, idx) => (
                  <div key={idx} style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                    <textarea
                      className="form-input"
                      rows={2}
                      value={rec}
                      onChange={(e) => handleUpdateRecital(idx, e.target.value)}
                      style={{ fontSize: "0.8rem", flex: 1, margin: 0 }}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveRecital(idx)}
                      style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", padding: "0.3rem" }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Points of Contact / Notices (Section 18) */}
            <div className="card glass-card" style={{ padding: "1rem", marginBottom: "1rem", border: "1px solid var(--border-color)" }}>
              <div style={{ marginBottom: "0.5rem" }}>
                <h4 style={{ margin: 0, fontWeight: 700, color: "var(--primary)", fontSize: "0.95rem" }}>
                  Default Points of Contact & Notices (Section 18)
                </h4>
                <p style={{ fontSize: "0.75rem", color: "var(--text-secondary)", margin: "0.2rem 0 0 0" }}>
                  Configure the default Notice contact block templates for Prime Contractor and Partner.
                </p>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: "0.78rem", fontWeight: 600 }}>
                    Prime Contractor Notice Block
                  </label>
                  <textarea
                    className="form-input"
                    rows={5}
                    value={editFormData.defaultLatNotices || ""}
                    onChange={(e) => setEditFormData({ ...editFormData, defaultLatNotices: e.target.value })}
                    style={{ fontSize: "0.8rem", lineHeight: 1.35, margin: 0 }}
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: "0.78rem", fontWeight: 600 }}>
                    Partner / Subcontractor Notice Block Template
                  </label>
                  <textarea
                    className="form-input"
                    rows={5}
                    value={editFormData.defaultPartnerNotices || ""}
                    onChange={(e) => setEditFormData({ ...editFormData, defaultPartnerNotices: e.target.value })}
                    placeholder="{{partnerCompany}}&#10;{{partnerAddress}}&#10;Attention: {{partnerPocName}}&#10;Phone: {{partnerPocPhone}}&#10;email: {{partnerPocEmail}}"
                    style={{ fontSize: "0.8rem", lineHeight: 1.35, margin: 0 }}
                  />
                </div>
              </div>
            </div>

            {/* Sections */}
            <div className="card glass-card" style={{ padding: "1rem", border: "1px solid var(--border-color)", marginBottom: "1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                <h4 style={{ margin: 0, fontWeight: 700, color: "var(--primary)", fontSize: "0.95rem" }}>
                  Numbered Clauses & Sections ({editFormData.sections.length})
                </h4>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ padding: "0.25rem 0.6rem", fontSize: "0.75rem" }}
                  onClick={handleAddSection}
                >
                  <Plus size={13} /> Add Section
                </button>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", maxHeight: "400px", overflowY: "auto", paddingRight: "0.5rem" }}>
                {editFormData.sections.map((sec, idx) => (
                  <div key={sec.id || idx} style={{ padding: "0.75rem", border: "1px solid var(--border-color)", borderRadius: "6px", backgroundColor: "rgba(255, 255, 255, 0.01)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: 1 }}>
                        <span style={{ fontWeight: 700, color: "var(--primary)", fontSize: "0.85rem" }}>
                          {sec.sectionNumber || idx + 1}.
                        </span>
                        <input
                          type="text"
                          className="form-input"
                          value={sec.title || ""}
                          onChange={(e) => handleUpdateSection(idx, "title", e.target.value)}
                          placeholder="Section Title"
                          style={{ margin: 0, padding: "0.2rem 0.5rem", fontSize: "0.85rem", fontWeight: 600, flex: 1, maxWidth: "350px" }}
                        />
                      </div>
                      <div style={{ display: "flex", gap: "0.25rem" }}>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ padding: "0.2rem 0.4rem", fontSize: "0.7rem" }}
                          disabled={idx === 0}
                          onClick={() => handleMoveSection(idx, -1)}
                        >
                          <ArrowUp size={12} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ padding: "0.2rem 0.4rem", fontSize: "0.7rem" }}
                          disabled={idx === editFormData.sections.length - 1}
                          onClick={() => handleMoveSection(idx, 1)}
                        >
                          <ArrowDown size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveSection(idx)}
                          style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", padding: "0.2rem 0.4rem" }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                    <textarea
                      className="form-input"
                      rows={3}
                      value={sec.content || ""}
                      onChange={(e) => handleUpdateSection(idx, "content", e.target.value)}
                      placeholder="Legal clause text..."
                      style={{ fontSize: "0.8rem", lineHeight: 1.35, margin: 0 }}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseEditor}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSaving}
                style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
              >
                <Save size={15} /> {isSaving ? "Saving..." : "Create Template"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* TEMPLATES LIST */}
      {loading ? (
        <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
          Loading agreement templates...
        </div>
      ) : templates.length === 0 ? (
        <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)", fontStyle: "italic" }}>
          No agreement templates found.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {templates.map((tpl) => {
            const isExpanded = expandedTemplateId === tpl.id;

            return (
              <div
                key={tpl.id}
                style={{
                  border: isExpanded ? "2px solid var(--primary)" : "1px solid var(--border-color)",
                  borderRadius: "8px",
                  backgroundColor: "rgba(255, 255, 255, 0.01)",
                  overflow: "hidden",
                  transition: "border-color 0.2s"
                }}
              >
                {/* Header Row */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "1rem 1.25rem",
                    backgroundColor: isExpanded ? "rgba(59, 130, 246, 0.04)" : "transparent"
                  }}
                >
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <h4 style={{ margin: 0, fontSize: "1rem", fontWeight: 600, color: "var(--text-primary)" }}>
                        {tpl.title}
                      </h4>
                      {tpl.isDefault && (
                        <span className="badge badge-primary" style={{ fontSize: "0.7rem", padding: "0.15rem 0.45rem" }}>
                          Default
                        </span>
                      )}
                      <span className="badge badge-secondary" style={{ fontSize: "0.7rem", padding: "0.15rem 0.45rem" }}>
                        {tpl.sections?.length || 0} Clauses
                      </span>
                    </div>
                    <p style={{ margin: "0.3rem 0 0 0", fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                      {tpl.description || "No description provided."}
                    </p>
                  </div>

                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button
                      type="button"
                      className={`btn ${isExpanded ? "btn-primary" : "btn-secondary"}`}
                      onClick={() => handleOpenEdit(tpl)}
                      style={{ padding: "0.35rem 0.75rem", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: "0.3rem" }}
                    >
                      {isExpanded ? (
                        <><ChevronUp size={14} /> Collapse</>
                      ) : (
                        <><Edit size={14} /> Edit Clauses <ChevronDown size={14} /></>
                      )}
                    </button>
                    {!tpl.isDefault && (
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => handleDelete(tpl)}
                        style={{ padding: "0.35rem 0.6rem", fontSize: "0.8rem", backgroundColor: "var(--danger)" }}
                        title="Delete Template"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {/* INLINE FOLD-OUT EXPANDED EDITOR */}
                {isExpanded && (
                  <div
                    className="animate-fade-in"
                    style={{
                      padding: "1.25rem",
                      borderTop: "1px solid var(--border-color)",
                      backgroundColor: "rgba(10, 12, 18, 0.3)"
                    }}
                  >
                    <form onSubmit={handleSaveSubmit}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label" style={{ fontWeight: 600 }}>Template Title *</label>
                          <input
                            type="text"
                            className="form-input"
                            value={editFormData.title}
                            onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                            required
                          />
                        </div>

                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label" style={{ fontWeight: 600 }}>Description</label>
                          <input
                            type="text"
                            className="form-input"
                            value={editFormData.description}
                            onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                          />
                        </div>
                      </div>

                      {/* Preamble */}
                      <div className="card glass-card" style={{ padding: "1rem", marginBottom: "1rem", border: "1px solid var(--border-color)" }}>
                        <label className="form-label" style={{ fontWeight: 700, color: "var(--primary)", marginBottom: "0.3rem" }}>
                          Preamble Opening Statement
                        </label>
                        <p style={{ fontSize: "0.75rem", color: "var(--text-secondary)", margin: "0 0 0.4rem 0" }}>
                          Supports placeholders: {"{{day}}"}, {"{{month}}"}, {"{{year}}"}, {"{{partnerName}}"}, {"{{partnerState}}"}, {"{{partnerAddress}}"}
                        </p>
                        <textarea
                          className="form-input"
                          rows={3}
                          value={editFormData.preambleTemplate}
                          onChange={(e) => setEditFormData({ ...editFormData, preambleTemplate: e.target.value })}
                          style={{ fontSize: "0.82rem", lineHeight: 1.4 }}
                        />
                      </div>

                      {/* Recitals */}
                      <div className="card glass-card" style={{ padding: "1rem", marginBottom: "1rem", border: "1px solid var(--border-color)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                          <label className="form-label" style={{ fontWeight: 700, color: "var(--primary)", margin: 0 }}>
                            Recitals (WHEREAS Clauses)
                          </label>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem" }}
                            onClick={handleAddRecital}
                          >
                            <Plus size={13} /> Add Recital
                          </button>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                          {editFormData.recitals.map((rec, idx) => (
                            <div key={idx} style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                              <textarea
                                className="form-input"
                                rows={2}
                                value={rec}
                                onChange={(e) => handleUpdateRecital(idx, e.target.value)}
                                style={{ fontSize: "0.8rem", flex: 1, margin: 0 }}
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveRecital(idx)}
                                style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", padding: "0.3rem" }}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Points of Contact / Notices (Section 18) */}
                      <div className="card glass-card" style={{ padding: "1rem", marginBottom: "1rem", border: "1px solid var(--border-color)" }}>
                        <div style={{ marginBottom: "0.5rem" }}>
                          <h4 style={{ margin: 0, fontWeight: 700, color: "var(--primary)", fontSize: "0.95rem" }}>
                            Default Points of Contact & Notices (Section 18)
                          </h4>
                          <p style={{ fontSize: "0.75rem", color: "var(--text-secondary)", margin: "0.2rem 0 0 0" }}>
                            Configure the default Notice contact block templates for Prime Contractor and Partner.
                          </p>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label" style={{ fontSize: "0.78rem", fontWeight: 600 }}>
                              Prime Contractor Notice Block
                            </label>
                            <textarea
                              className="form-input"
                              rows={5}
                              value={editFormData.defaultLatNotices || ""}
                              onChange={(e) => setEditFormData({ ...editFormData, defaultLatNotices: e.target.value })}
                              style={{ fontSize: "0.8rem", lineHeight: 1.35, margin: 0 }}
                            />
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label" style={{ fontSize: "0.78rem", fontWeight: 600 }}>
                              Partner / Subcontractor Notice Block Template
                            </label>
                            <textarea
                              className="form-input"
                              rows={5}
                              value={editFormData.defaultPartnerNotices || ""}
                              onChange={(e) => setEditFormData({ ...editFormData, defaultPartnerNotices: e.target.value })}
                              placeholder="{{partnerCompany}}&#10;{{partnerAddress}}&#10;Attention: {{partnerPocName}}&#10;Phone: {{partnerPocPhone}}&#10;email: {{partnerPocEmail}}"
                              style={{ fontSize: "0.8rem", lineHeight: 1.35, margin: 0 }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Sections / Clauses */}
                      <div className="card glass-card" style={{ padding: "1rem", border: "1px solid var(--border-color)", marginBottom: "1rem" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                          <h4 style={{ margin: 0, fontWeight: 700, color: "var(--primary)", fontSize: "0.95rem" }}>
                            Numbered Clauses & Sections ({editFormData.sections.length})
                          </h4>
                          <button
                            type="button"
                            className="btn btn-primary"
                            style={{ padding: "0.25rem 0.6rem", fontSize: "0.75rem" }}
                            onClick={handleAddSection}
                          >
                            <Plus size={13} /> Add Section
                          </button>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", maxHeight: "450px", overflowY: "auto", paddingRight: "0.5rem" }}>
                          {editFormData.sections.map((sec, idx) => (
                            <div key={sec.id || idx} style={{ padding: "0.75rem", border: "1px solid var(--border-color)", borderRadius: "6px", backgroundColor: "rgba(255, 255, 255, 0.01)" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: 1 }}>
                                  <span style={{ fontWeight: 700, color: "var(--primary)", fontSize: "0.85rem" }}>
                                    {sec.sectionNumber || idx + 1}.
                                  </span>
                                  <input
                                    type="text"
                                    className="form-input"
                                    value={sec.title || ""}
                                    onChange={(e) => handleUpdateSection(idx, "title", e.target.value)}
                                    placeholder="Section Title"
                                    style={{ margin: 0, padding: "0.2rem 0.5rem", fontSize: "0.85rem", fontWeight: 600, flex: 1, maxWidth: "350px" }}
                                  />
                                </div>
                                <div style={{ display: "flex", gap: "0.25rem" }}>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ padding: "0.2rem 0.4rem", fontSize: "0.7rem" }}
                                    disabled={idx === 0}
                                    onClick={() => handleMoveSection(idx, -1)}
                                    title="Move Up"
                                  >
                                    <ArrowUp size={12} />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ padding: "0.2rem 0.4rem", fontSize: "0.7rem" }}
                                    disabled={idx === editFormData.sections.length - 1}
                                    onClick={() => handleMoveSection(idx, 1)}
                                    title="Move Down"
                                  >
                                    <ArrowDown size={12} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveSection(idx)}
                                    style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", padding: "0.2rem 0.4rem" }}
                                    title="Delete Section"
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                </div>
                              </div>
                              <textarea
                                className="form-input"
                                rows={3}
                                value={sec.content || ""}
                                onChange={(e) => handleUpdateSection(idx, "content", e.target.value)}
                                placeholder="Legal clause text..."
                                style={{ fontSize: "0.8rem", lineHeight: 1.35, margin: 0 }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleCloseEditor}
                          disabled={isSaving}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isSaving}
                          style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                        >
                          <Save size={15} /> {isSaving ? "Saving..." : "Save Template Changes"}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
